import { getSmartFieldProps } from './fields';

const defaultDetailFieldProps = () => ({
    hidden: false,
    readOnly: false,
    mandatory: false,
    visible: true,
});

/**
 * Resolves a field entry from mappedSchema by id or fieldConfiguration (case-insensitive).
 */
export const resolveMappedSchemaField = (fieldId, mappedSchema) => {
    if (!fieldId || !mappedSchema) return null;

    const direct = mappedSchema[fieldId];
    if (direct) return direct;

    return (
        Object.values(mappedSchema).find(
            (entry) =>
                entry?.id === fieldId ||
                entry?.fieldConfiguration === fieldId ||
                String(entry?.id).toLowerCase() === String(fieldId).toLowerCase() ||
                String(entry?.fieldConfiguration).toLowerCase() === String(fieldId).toLowerCase(),
        ) || null
    );
};

/**
 * Resolves effective visibility/read-only/mandatory for a detail field, mirroring EntityCrudTab.
 */
export const getDetailFieldEffectiveProps = ({
    field,
    data,
    configMap,
    dynamicHiddenMap = {},
    isNewEntity = false,
}) => {
    if (!field) {
        return { hidden: true, readOnly: false, mandatory: false, visible: false };
    }

    const smartFieldOverrides =
        configMap && data ? getSmartFieldProps({ field, configMap, data, isNewEntity }) : {};

    let hidden = false;
    if (Object.prototype.hasOwnProperty.call(smartFieldOverrides, 'hidden')) {
        hidden = Boolean(smartFieldOverrides.hidden);
    } else if (field.hidden) {
        hidden = true;
    } else if (dynamicHiddenMap?.[field.fieldConfiguration]) {
        hidden = true;
    }

    const readOnly =
        smartFieldOverrides.readOnly !== undefined
            ? smartFieldOverrides.readOnly
            : Boolean(field.readOnly);

    const mandatory =
        smartFieldOverrides.mandatory !== undefined
            ? smartFieldOverrides.mandatory
            : Boolean(field.mandatory);

    return {
        hidden,
        readOnly,
        mandatory,
        visible: !hidden,
    };
};

export const isDetailFieldVisible = (props) => !props?.hidden;

export const isDetailFieldReadOnly = (props) => Boolean(props?.readOnly);

/**
 * Combines standardFieldsSchemaMap `isHidden` with client SmartField/dynamicHiddenMap rules.
 * Use for any detail standard field — SmartFields can apply to any schema field.
 *
 * @param {Object|null|undefined} schemaField - Entry from standardFieldsSchemaMap (has isHidden)
 * @param {string} fieldId - Client mappedSchema id (or fieldConfiguration) for dynamism lookup
 * @param {(fieldId: string) => boolean} isFieldVisible - From useDetailFieldDynamism / createDetailDynamismHelpers
 */
export const isDetailStandardFieldVisible = (schemaField, fieldId, isFieldVisible) => {
    const configVisible = schemaField == null || !schemaField.isHidden;
    if (!configVisible) return false;
    if (!fieldId || typeof isFieldVisible !== 'function') return true;
    return isFieldVisible(fieldId);
};

/**
 * True when shadow CRUD state is populated and not still loading.
 * Empty `data` (e.g. mid `ENTITY_CRUD_INIT` re-init) is treated as not ready.
 */
export const isShadowCrudReadyState = ({ mappedSchema, configMap, data, loading }) =>
    Boolean(
        mappedSchema &&
        configMap &&
        data &&
        typeof data === 'object' &&
        Object.keys(data).length > 0 &&
        !loading,
    );

export const createDetailDynamismHelpers = ({
    crudData,
    configMap,
    mappedSchema,
    dynamicHiddenMap,
    isNewEntity = false,
}) => {
    const data = crudData || {};

    const getFieldProps = (fieldId) =>
        getDetailFieldPropsById(fieldId, {
            data,
            configMap,
            mappedSchema,
            dynamicHiddenMap,
            isNewEntity,
        });

    return {
        getFieldProps,
        isFieldVisible: (fieldId) => isDetailFieldVisible(getFieldProps(fieldId)),
        isFieldReadOnly: (fieldId) => isDetailFieldReadOnly(getFieldProps(fieldId)),
        isStandardFieldVisible: (schemaField, fieldId) =>
            isDetailStandardFieldVisible(schemaField, fieldId, (id) =>
                isDetailFieldVisible(getFieldProps(id)),
            ),
    };
};

export const getDetailFieldPropsById = (
    fieldId,
    { data, configMap, mappedSchema, dynamicHiddenMap, isNewEntity = false } = {},
) => {
    if (!fieldId || !mappedSchema || !data || !configMap) {
        return defaultDetailFieldProps();
    }

    const field = resolveMappedSchemaField(fieldId, mappedSchema);
    if (!field) {
        return defaultDetailFieldProps();
    }

    return getDetailFieldEffectiveProps({
        field,
        data,
        configMap,
        dynamicHiddenMap,
        isNewEntity,
    });
};

const resolveExtraConfigFromSchemaMap = (entityExtraFields, fieldId) => {
    if (!entityExtraFields || !fieldId) return null;

    const direct = entityExtraFields[fieldId] || entityExtraFields[String(fieldId).toLowerCase()];
    if (direct) return direct;

    const normalizedFieldId = String(fieldId).toLowerCase();
    const matchedKey = Object.keys(entityExtraFields).find(
        (key) => String(key).toLowerCase() === normalizedFieldId,
    );

    return matchedKey ? entityExtraFields[matchedKey] : null;
};

const resolveExtraFieldSchema = (
    field,
    mappedSchema,
    extraFieldsSchemaMap,
    entityExtraFieldName,
) => {
    const fieldId = field?.id;
    if (!fieldId) return null;

    const schemaField = resolveMappedSchemaField(fieldId, mappedSchema);
    if (schemaField) return schemaField;

    if (!extraFieldsSchemaMap || !entityExtraFieldName) return null;

    const extraConfig = resolveExtraConfigFromSchemaMap(
        extraFieldsSchemaMap[entityExtraFieldName],
        fieldId,
    );

    if (!extraConfig) return null;

    return {
        id: fieldId,
        fieldConfiguration: fieldId,
        ...extraConfig,
        dynamicFrom: extraConfig.TiposEntity || extraConfig.dynamicFrom,
        dynamicFromIds: extraConfig.strTiposEntity
            ? String(extraConfig.strTiposEntity)
                  .split(';')
                  .map((value) => value.trim())
                  .filter(Boolean)
            : extraConfig.dynamicFromIds,
        smartField: extraConfig.smartField,
    };
};

const isBackendDynamismHidden = (field) =>
    field?.hiddenByDynamism === true || field?.hiddenByDynamism === 'True';

/**
 * Filters detail extra/custom fields using the same client dynamism rules as CRUD when shadow state exists.
 */
export const filterDetailExtraFieldsByDynamism = (
    fields,
    {
        mappedSchema,
        data,
        configMap,
        dynamicHiddenMap,
        isNewEntity = false,
        extraFieldsSchemaMap,
        entityExtraFieldName,
        loading,
    } = {},
) => {
    if (!Array.isArray(fields)) return [];

    const hasShadowCrud = isShadowCrudReadyState({ mappedSchema, configMap, data, loading });

    return fields.filter((field) => {
        if (!hasShadowCrud) {
            return !isBackendDynamismHidden(field);
        }

        const schemaField = resolveExtraFieldSchema(
            field,
            mappedSchema,
            extraFieldsSchemaMap,
            entityExtraFieldName,
        );

        if (!schemaField) {
            return !isBackendDynamismHidden(field);
        }

        return isDetailFieldVisible(
            getDetailFieldEffectiveProps({
                field: schemaField,
                data,
                configMap,
                dynamicHiddenMap,
                isNewEntity,
            }),
        );
    });
};
